<template>
    <div class="table-responsive">
        <table class="table text-nowrap align-middle mb-0" v-if="therapies && therapies.length != 0">
            <thead>
                <tr>
                    <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">
                        {{ $t('feature.therapy.drug') }}
                    </th>
                    <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0 text-end">
                        {{ $t('feature.therapy.dose') }}
                    </th>
                    <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0 text-end">
                        {{ $t('form.start_at.label') }}
                    </th>
                    <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0 text-end">
                        {{ $t('form.end_at.label') }}
                    </th>
                    <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0 text-end">
                        {{ $t('feature.therapy.prn') }}
                    </th>
                    <template v-if="show_stats">
                        <th scope="col"
                            class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0 text-end">
                            {{ $t(`feature.therapy.intake.$status.${IntakeStatus.taken_in_time}`) }}
                        </th>
                        <th scope="col" v-if="env === 'sifi_fk' || env === 'fungalk'"
                            class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0 text-end">
                            {{ $t(`feature.therapy.intake.$status.${IntakeStatus.taken_not_in_time}`) }}
                        </th>
                        <th scope="col"
                            class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0 text-end">
                            {{ $t(`feature.therapy.intake.$status.${IntakeStatus.not_taken}`) }}
                        </th>
                    </template>
                    <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0 text-end"
                        v-if="show_actions">
                    </th>
                    <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0 text-end" v-if="therapy_trend_enabled">
                        <router-link
                            :to="{ path: `/patient/${patient_id}/therapy/trend`, query: { range_type: 'week', from: range.from, to: range.to } }"
                            v-if="show_overall_trend">
                            <button class="dropdown-toggle lh-1 bg-transparent border-0 shadow-none p-0 transition"
                                type="button">
                                <i class="ph-duotone ph-chart-line"></i>
                            </button>
                        </router-link>
                    </th>
                </tr>
            </thead>
            <tbody>
                <!-- CENTER ROW -->
                <tr v-for="therapy in therapies" :key="therapy.id">
                    <td class="shadow-none lh-1 fw-medium text-primary">
                        {{ therapy.scheme?.drug ?? '' }}

                        <template v-if="getSchemeRule(therapy.scheme as TherapyScheme)?.intake_per_day !== undefined">
                            -
                            {{ $tc('feature.therapy.intakes_per_day',
                                getSchemeRule(therapy.scheme as TherapyScheme)!.intake_per_day) }}
                        </template>
                    </td>
                    <td class="shadow-none lh-1 fw-medium text-body-tertiary text-end">
                        {{ therapy.dose }}
                    </td>
                    <td class="shadow-none lh-1 fw-medium text-body-tertiary text-end">
                        {{ YYYYMMDD(therapy.start_at) }}
                    </td>
                    <td class="shadow-none lh-1 fw-medium text-body-tertiary text-end">
                        <i v-if="therapy.previous_stop_at" class="ph-bold ph-prohibit fs-12 opacity-50"></i> {{
                            YYYYMMDD(therapy.stop_at) }}
                    </td>
                    <td class="shadow-none lh-1 fw-medium text-body-tertiary text-end">
                        <div class="form-check form-switch d-inline-block">
                            <input class="form-check-input shadow-none" type="checkbox" role="switch"
                                :checked="therapy.is_prn" disabled>
                        </div>
                    </td>
                    <template v-if="show_stats">
                        <td class="shadow-none lh-1 fw-medium text-body-tertiary text-end">
                            {{ therapy.taken_in_time }}
                        </td>
                        <td v-if="env === 'sifi_fk' || env === 'fungalk'" class="shadow-none lh-1 fw-medium text-body-tertiary text-end">
                            {{ therapy.taken_not_in_time }}
                        </td>
                        <td class="shadow-none lh-1 fw-medium text-body-tertiary text-end">
                            {{ therapy.not_taken }}
                        </td>
                    </template>
                    <td class="shadow-none lh-1 fw-medium text-body-tertiary text-end" v-if="show_actions">
                        <SpinnerLoading v-if="loading === therapy.id" class="h-auto" :small="true" />
                        <div class="dropdown position-static"
                            v-else-if="(is_active_therapy(therapy) || is_future_therapy(therapy))">
                            <button class="dropdown-toggle lh-1 bg-transparent border-0 shadow-none p-0 transition"
                                type="button" data-bs-toggle="dropdown" aria-expanded="false"
                                data-bs-boundary="viewport">
                                <i class="flaticon-dots"></i>
                            </button>
                            <ul class="dropdown-menu">
                                <li v-if="is_active_therapy(therapy)">
                                    <button class="dropdown-item d-flex align-items-center"
                                        v-on:click="confirmAction(therapy, 'stop')">
                                        <i class="ph ph-pause lh-1 me-8 position-relative top-1"></i>
                                        {{ $t('cta.stop') }}
                                    </button>
                                </li>
                                <li v-if="is_future_therapy(therapy)">
                                    <button class="dropdown-item d-flex align-items-center"
                                        v-on:click="confirmAction(therapy, 'delete')">
                                        <i class="ph ph-trash lh-1 me-8 position-relative top-1"></i>
                                        {{ $t('cta.delete') }}
                                    </button>
                                </li>
                            </ul>
                        </div>
                    </td>
                    <td class="shadow-none lh-1 fw-medium text-body-tertiary text-end" v-if="therapy_trend_enabled">
                        <router-link v-if="!therapy.is_prn"
                            :to="{ path: `/patient/${patient_id}/therapy/${therapy.id}/trend`, query: { range_type: 'week', from: YYYYMMDD(therapy.start_at), to: YYYYMMDD(therapy.stop_at) } }">
                            <button class="dropdown-toggle lh-1 bg-transparent border-0 shadow-none p-0 transition"
                                type="button">
                                <i class="ph-bold ph-chart-line"></i>
                            </button>
                        </router-link>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
    <ConfirmModal :id="`therapies_${ids}`" @confirm="therapyAction()" :title="$t('cta.$delete.title')"
        :message="$t('cta.$delete.message')" />
</template>
<script lang="ts">
import { defineComponent } from 'vue';
import { IntakeStatus, Therapy, TherapyScheme, TherapySchemeRule } from '@/utils/models/therapy.model';
import { PropType } from 'vue';
import { PatientInfo } from '@/utils/models/user.model';

import DateFormatUtils from '@/utils/methods/date_formats';
import config, { Environment } from '@/utils/const/config';
import { CLIENT } from '@/utils/const/env';
import { is_active_therapy, is_future_therapy } from '@/utils/methods/therapy_dates';
import { Modal } from 'bootstrap';
import ConfirmModal from '@/components/Widgets/ConfirmModal.vue';
import API from '@/services/api/api.service';
import SpinnerLoading from '@/components/Widgets/Spinner.vue';
import dayjs from 'dayjs';


export default defineComponent({
    name: 'TherapyList',
    components: {
        ConfirmModal,
        SpinnerLoading
    },
    props: {
        therapies: {
            type: Object as PropType<Therapy[]>,
            required: true
        },
        patient: {
            type: Object as PropType<PatientInfo>,
            required: true
        },
        show_stats: {
            type: Boolean,
            default: false as boolean,
        },
        show_actions: {
            type: Boolean,
            default: false as boolean,
        },
        show_overall_trend: {
            type: Boolean,
            default: false as boolean,
        },
    },
    data() {
        return {
            confirmModal: null,
            selectedTherapy: null,
            action: null,
            loading: null,
            ids: this.therapies.length === 0 ? 'empty' : this.therapies.map((t) => t.id).reduce((a, b) => `${a}_${b}`),
        } as {
            confirmModal: Modal | null,
            action: 'stop' | 'delete' | null,
            selectedTherapy: Therapy | null;
            loading: string | null;
            ids: string;
        }
    },
    mounted() {
        this.confirmModal = new Modal(`#therapies_${this.ids}`, {})
    },
    emits: ['stop', 'delete'],
    computed: {
        status() { return IntakeStatus },
        YYYYMMDD() {
            return DateFormatUtils.YYYYMMDD;
        },
        IntakeStatus() { return IntakeStatus },
        env(): Environment { return CLIENT },
        is_active_therapy() { return is_active_therapy },
        is_future_therapy() { return is_future_therapy },
        patient_id(): string { return this.$route.params.id as string },
        range(): { from: string, to: string } {
            return {
                from: DateFormatUtils.YYYYMMDD(dayjs(Math.min(...this.therapies.map((therapy) => dayjs(therapy.start_at).valueOf()))).toDate()),
                to: DateFormatUtils.YYYYMMDD(dayjs(Math.max(...this.therapies.map((therapy) => dayjs(therapy.stop_at).valueOf()))).toDate())
            }
        },
        therapy_trend_enabled(): boolean {
            return config.feature.therapy_trend;
        }
    },
    methods: {
        confirmAction(therapy: Therapy, action: 'stop' | 'delete') {
            this.selectedTherapy = therapy;
            this.action = action;
            this.confirmModal?.show();
        },
        therapyAction() {
            switch (this.action) {
                case 'stop':
                    this.stopTherapy(this.selectedTherapy!);
                    break;
                case 'delete':
                    this.deleteTherapy(this.selectedTherapy!);
                    break;

                default:
                    break;
            }
            this.selectedTherapy = null;
            this.action = null;
        },
        stopTherapy(therapy: Therapy) {
            this.loading = therapy.id;
            API.stopTherapy({ patient_id: this.patient.id!, therapy_id: therapy.id, stop_at: dayjs().toDate() }).then((response) => {
                if (response.data.success === true) {
                    this.$emit('stop', response.data.data);
                }
            }).catch((error) => { }).finally(() => { this.loading = null });
        },
        deleteTherapy(therapy: Therapy) {
            this.loading = therapy.id;
            API.deleteTherapy({ patient_id: this.patient.id!, therapy_id: therapy.id }).then((response) => {
                if (response.data.success === true) {
                    this.$emit('delete', therapy);
                }
            }).catch((error) => { }).finally(() => { this.loading = null });
        },
        getSchemeRule(scheme: TherapyScheme): TherapySchemeRule | undefined {
            return [...(scheme.rules ?? [])].shift();
        }
    }
});
</script>